import type { IBackgroundStyle, IBorderStyle, IColorStyle, IFontStyle } from "../Styles";
import type { IDomWrapable } from "../../interfaces/IDomWrapable";
import type { EventHandler } from "../../interfaces/EventHandler";
import type { Node } from "../Core/Node";
import { Group } from "../Core/Group";
import { Container } from "../Dom/Container";

/**
 * 定义了一个 DOM 元素的包装器类，提供了一些方法来操作和管理 DOM 元素的样式、位置、旋转、缩放等属性
 * 其中关于 DOM 元素的定位，是基于父元素是已定位(非 static)，子元素是绝对定位的原则
 * 在 CSS 布局中，当涉及到绝对定位的子元素与已定位父元素的关系时，定位参考基准如下(由浏览器决定的）：
 * 父元素：绝对定位子元素的参考点是父元素的 padding box(即 border 的内边缘)
 * 子元素：子元素的 margin 外边缘是被定位的部分
 */

export class DomWrapper implements IDomWrapable {

    protected _htmlEle: HTMLElement;
    private _node: Node;

    private _rotation: number = 0; // 存储当前旋转角度（弧度）
    private _scaleX: number = 1; // 存储当前X轴缩放比例
    private _scaleY: number = 1; // 存储当前Y轴缩放比例

    get htmlEle(): HTMLElement { return this._htmlEle; }
    get node(): Node { return this._node; }

    constructor(node: Node, dom: HTMLElement) {
        this._htmlEle = dom;
        this._node = node;

        // 同步 Node ID 到 DOM Element ID
        if (this._node.id) {
            // 如果 DOM 元素还没有 ID，或者 ID 与 Node 的 ID 不同，则设置它
            if (!this._htmlEle.id || this._htmlEle.id !== this._node.id) {
                this._htmlEle.id = this._node.id;
            }
        }

        this.htmlEle.style.transformOrigin = '0 0'; // 设置变换原点为左上角

        // 应用基本样式（不依赖parent）
        this.applyBaseStyles();

        this.setLevel(node.level); // 设置层级
    }

    // 应用基本样式
    private applyBaseStyles(): void {
        // 所有元素都应用的基本样式
        this.htmlEle.style.boxSizing = 'border-box';
        this.htmlEle.style.margin = '0';

        if (this._node instanceof Group) {
            this.setOverflow(this._node.overflowable); // 设置溢出属性

            // 去掉右键菜单
            this.htmlEle.addEventListener('contextmenu', (event) => {
                event.preventDefault();
                return false;
            });
        } else {
            // 普通元素默认设为绝对定位
            this.htmlEle.style.position = 'absolute';
        }
    }

    // 更新定位样式
    updatePositioningStyle(): void {
        if (this._node.parent) {
            // 有父节点时使用绝对定位
            this.htmlEle.style.position = 'absolute';
            if (this._node instanceof Group) {
                this.htmlEle.style.border = 'none'; // 去除嵌套组边框
            }
        } else {
            // 是根节点时，如果没有设置定位，则设置为相对定位
            const computedStyle = window.getComputedStyle(this.htmlEle);
            if (computedStyle.position === 'static') {
                this.htmlEle.style.position = 'relative';
            }
        }
    }

    // 更新 DOM 元素的 transform 属性
    private _updateTransform(): void {
        let transform = '';
        // 按照固定顺序应用变换（先旋转后缩放）
        if (this._rotation !== 0) {
            transform += `rotate(${this._rotation}rad) `;
        }
        if (this._scaleX !== 1 || this._scaleY !== 1) {
            transform += `scale(${this._scaleX}, ${this._scaleY})`;
        }
        this._htmlEle.style.transform = transform.trim();
    }

    // 实现 IDomWrapable 接口方法
    addEventListener(
        event: string,
        handler: (event: any) => void,
        options?: boolean | { passive?: boolean; }
    ): void {
        this._htmlEle.addEventListener(event, handler, options);
    }

    // 实现 IDomWrapable 接口方法
    removeEventListener(event: string, handler: EventHandler): void {
        this._htmlEle.removeEventListener(event, handler);
    }

    // 设置层级
    setLevel(level: number): void {
        if (level > 0) {
            this._htmlEle.style.zIndex = level.toString();
        }
    }

    setOverflow(overflowable: boolean): void {
        this._htmlEle.style.overflow = overflowable ? 'visible' : 'hidden';
    }

    moveTo(x: number, y: number): void {
        this._htmlEle.style.left = x + "px";
        this._htmlEle.style.top = y + "px";
    }

    rotateTo(angle: number): void {
        this._rotation = angle;
        this._updateTransform();
    }

    scaleTo(sx: number, sy: number): void {
        this._scaleX = sx;
        this._scaleY = sy;
        this._updateTransform();
    }

    setWidth(width: number): void {
        this.setSize({ width });
    }

    setHeight(height: number): void {
        this.setSize({ height });
    }

    setSize(size: { width?: number, height?: number }): void {
        const { width, height } = size;
        if (width !== undefined) this._htmlEle.style.width = width + 'px';
        if (height !== undefined) this._htmlEle.style.height = height + 'px';
    }

    get offsetWidth(): number {
        return this._htmlEle.offsetWidth;
    }

    get offsetHeight(): number {
        return this._htmlEle.offsetHeight;
    }

    getBoundingClientRect() {
        return this._htmlEle.getBoundingClientRect();
    }

    applyFont(font: IFontStyle): void {
        const { fontFamily, fontSize, fontStyle, fontWeight } = font;
        if (fontFamily) {
            this._htmlEle.style.fontFamily = fontFamily;
        }
        if (fontSize) {
            this._htmlEle.style.fontSize = fontSize + 'px';
        }
        if (fontStyle) {
            this._htmlEle.style.fontStyle = fontStyle;
        }
        if (fontWeight) {
            this._htmlEle.style.fontWeight = fontWeight;
        }
    }

    applyColor(colorStyle: IColorStyle): void {
        if (colorStyle.color) {
            this._htmlEle.style.color = colorStyle.color;
        }
    }

    applyBorder(border: IBorderStyle): void {
        const { borderWidth, borderColor, borderStyle } = border;
        if (borderWidth) {
            this._htmlEle.style.borderWidth = borderWidth + 'px';
        }
        if (borderColor) {
            this._htmlEle.style.borderColor = borderColor;
        }
        if (borderStyle) {
            this._htmlEle.style.borderStyle = borderStyle;
        }
    }

    applyBackground(backgroundStyle: IBackgroundStyle): void {
        const { backgroundColor, opacity } = backgroundStyle;
        if (backgroundColor) {
            this._htmlEle.style.backgroundColor = backgroundColor;
        }
        if (opacity !== undefined) {
            this._htmlEle.style.opacity = opacity.toString();
        }
    }
}